import { toast } from "sonner";
/* eslint-disable jsx-a11y/anchor-is-valid */
import * as React from "react";
import {useEffect, useState} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {useNavigate} from "react-router";
import {Order} from "../../helpers/TableHelpers";
import {
  MigrationStatus,
  MigrationStatusColors,
  MigrationStatusIcons,
  MigrationStatusMigrationEntityLabels,
} from "../../models/MigrationStatus";
import {FlagType} from "../../models/ClientConfiguration/FlaggingConfiguration";
import { colorVariantClasses } from "@/theme/AppColorVariant";
import {FilterCombobox} from "../common/FilterCombobox";
import MigrationStatusChip from "../common/MigrationStatusChip";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import {JobRequestStatus} from "../../models/Jobs/JobRequestStatus";
import {DeleteProviderRequest} from "../../models/Jobs/JobRequest";
import {JobType} from "../../models/Jobs/JobType";
import {JobEntityType} from "../../models/Jobs/JobEntityType";
import FlagTypeChip from "../common/FlagTypeChip";
import {Provider} from "../../models/Provider";
import {ActiveProviderJobsExplorer} from "../../pages/ProvidersPage";
import ProvidersRepository from "../../repositories/ProvidersRepository";
import {UserRole} from "../../models/User";
import ConfirmMigrationEntityBlockUpdateModal, {
  MIGRATION_ENTITY_BLOCK_UPDATE_CONFIGS
} from "../common/ConfirmMigrationEntityBlockUpdateModal";
import TextHighlighter from "../common/TextHighlighter";
import {cn} from "@/lib/utils";
import {Table, TableHeader, TableBody, TableRow, TableHead, TableCell} from "@/components/ui/table";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip";
import {Button} from "@/components/ui/button";
import {Label} from "@/components/ui/label";
import {Badge} from "@/components/ui/badge";
import {Trash2, FileSpreadsheet, Lock, LockOpen, Ban, FileWarning, AlertTriangle, CheckCircle2} from "lucide-react";
import {
  fileFiltersBoxClassName,
  filterComboboxClassName,
  tableWrapperClassName,
} from "../common/Table/TableStyles";
import { SearchInput } from "../common/Table/SearchInput";
import { FlagStatusFilter, useFlagStatusFilter } from "../common/Table/FlagStatusFilter";
import TablePagination from "../common/Table/TablePagination";
import TableEmptyState from "../common/TableEmptyState";
import { useUserLookup } from "../../hooks/useUserLookup";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { deletionWouldStrandUploads, STRANDED_UPLOADS_HINT } from "../../lib/migrationEntityDeletion";

interface ProvidersTableProps {
  organizationId: string;
  activeJobsExplorer: ActiveProviderJobsExplorer;
  newProvidersForDeletion: DeleteProviderRequest[];
  setNewProvidersForDeletion: React.Dispatch<React.SetStateAction<DeleteProviderRequest[]>>;
  newProvidersForExport: Provider[];
  setNewProvidersForExport: React.Dispatch<React.SetStateAction<Provider[]>>;
  userRole: UserRole;
  isAssignedUser: boolean;
}

const ProvidersTable: React.FC<ProvidersTableProps> = ({
  organizationId,
  activeJobsExplorer,
  newProvidersForDeletion,
  setNewProvidersForDeletion,
  newProvidersForExport,
  setNewProvidersForExport,
  userRole,
  isAssignedUser
}) => {
  const { getUserName } = useUserLookup();
  const [order, setOrder] = React.useState<Order>("asc");
  const [sort, setSort] = React.useState<string>("status");
  const [lockFilter, setLockFilter] = React.useState<boolean | null>(null);
  const [statusFilter, setStatusFilter] = React.useState<string | null>(null);
  const [hasFlaggedPathsFilter, setHasFlaggedPathsFilter] = React.useState<boolean | null>(null);
  const [conflictsFilter, setConflictsFilter] = React.useState<boolean | null>(null);
  const [searchForProviderNameId, setSearchForProviderNameId] = useState<string | null>("");
  const [page, setPage] = React.useState(0);
  const [flagStatusFilter, setFlagStatusFilter] = useFlagStatusFilter(() => setPage(0));
  const [rowsPerPage, setRowsPerPage] = React.useState(50);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [rows, setRows] = useState(0);
  const [dirtyProviderId, setDirtyProviderId] = useState<string>("");
  const [dirtyProviderNameId, setDirtyProviderNameId] = useState<string>("");
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = React.useState<boolean>(false);
  const providersRepository = new ProvidersRepository();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const providersQuery = useQuery({ queryKey: [
      QUERY_KEYS.PROVIDERS,
      organizationId,
      searchForProviderNameId,
      page,
      rowsPerPage,
      statusFilter,
      lockFilter,
      hasFlaggedPathsFilter,
      conflictsFilter,
      flagStatusFilter,
      sort,
      order,
    ], queryFn:
    async () => {
      return await providersRepository.getProviders({
        organizationId: organizationId,
        providerNameId: searchForProviderNameId
          ? [searchForProviderNameId]
          : null,
        descending: order === "desc",
        orderBy: sort,
        status: statusFilter ? [statusFilter] : null,
        isLocked: lockFilter,
        hasFlaggedPaths: hasFlaggedPathsFilter,
        hasConflicts: conflictsFilter,
        flagStatus: flagStatusFilter ? [flagStatusFilter] : null,
        pageNumber: page + 1,
        pageSize: rowsPerPage,
      });
    }, staleTime: 5 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000 + 1,
      refetchIntervalInBackground: true,
      refetchOnMount: true, });

  useEffect(() => {
    if (providersQuery.data?.data) {
      setProviders(providersQuery.data?.data);
      setRows(providersQuery.data.paging?.totalCount ?? 0);
    }
  }, [providersQuery]);

  const handleSearch = (newProviderNameId: string) => {
    setSearchForProviderNameId(newProviderNameId ?? null);
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const changeSort = (newSort: string) => {
    setPage(0);
    setSort(newSort);
  };

  const changeOrder = (newOrder: Order) => {
    setPage(0);
    setOrder(newOrder);
  };

  const handleDelete = (providerId: string, providerNameId: string) => {
    if (!disabledRows.has(providerId)) {
      setNewProvidersForDeletion((prevRows) => [
        ...prevRows,
        {
          migrationEntityId: providerId,
          providerNameId: providerNameId,
          status: JobRequestStatus.New,
          type: JobType.Delete,
          entityType: JobEntityType.Provider,
          organizationId: organizationId,
        } as DeleteProviderRequest,
      ]);
    }
  };

  const handleAddProviderForExport = (providerEntity: Provider) => {
    if (!disabledRows.has(providerEntity.id)) {
      setNewProvidersForExport((prevRows) => [...prevRows, providerEntity]);
    }
  };

  const getDisabledRows = () => {
    switch (activeJobsExplorer) {
      case ActiveProviderJobsExplorer.DeleteProviders:
        return new Set(newProvidersForDeletion.map((c) => c.migrationEntityId));
      case ActiveProviderJobsExplorer.ExportCsv:
        return new Set(newProvidersForExport.map((c) => c.id));
      default:
        return new Set<string>();
    }
  };

  const getActiveButtonForExplorer = (
    providerEntity: Provider,
    isDisabled: boolean
  ) => {
    if (userRole === UserRole.Viewer)
      return <></>;
    switch (activeJobsExplorer) {
      case ActiveProviderJobsExplorer.DeleteProviders:
        return (
          <span title={deletionWouldStrandUploads(providerEntity) ? STRANDED_UPLOADS_HINT : undefined}>
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-destructive"
              aria-label="Delete"
              disabled={isDisabled || deletionWouldStrandUploads(providerEntity)}
              onClick={() => handleDelete(providerEntity.id, providerEntity.providerNameId)}
            >
              <Trash2 className="size-4" />
            </Button>
          </span>
        );
      case ActiveProviderJobsExplorer.ExportCsv:
        return (
          <Button
            variant="ghost"
            size="icon-sm"
            className="text-emerald-600"
            aria-label="Csv"
            disabled={isDisabled}
            onClick={() => handleAddProviderForExport(providerEntity)}
          >
            <FileSpreadsheet className="size-4" />
          </Button>
        );
      default:
        <></>;
    }
  };

  const renderFilters = () => (
    <React.Fragment>
      <div className={filterComboboxClassName}>
        <Label className="text-xs font-semibold text-muted-foreground">Status</Label>
        <FilterCombobox
          value={statusFilter}
          onChange={(newValue) => {
            setPage(0);
            setStatusFilter(newValue);
          }}
          placeholder="Status"
          options={Object.values(MigrationStatus)
            .filter((v) =>
              v !== MigrationStatus.NotFound
              && v !== MigrationStatus.InterruptedDelta
              && v !== MigrationStatus.NotFoundMarkedForRetry
              && v !== MigrationStatus.SkippedDuplicate)
            .map((status) => ({
              value: status,
              label: MigrationStatusMigrationEntityLabels[status as MigrationStatus],
              color: MigrationStatusColors[status as MigrationStatus],
              icon: MigrationStatusIcons[status as MigrationStatus],
            }))}
        />
      </div>
      <div className={filterComboboxClassName}>
        <Label className="text-xs font-semibold text-muted-foreground">Lock</Label>
        <FilterCombobox
          value={lockFilter === null ? null : lockFilter ? "locked" : "unlocked"}
          onChange={(newValue) => {
            setPage(0);
            if (newValue === null || newValue === "all") {
              setLockFilter(null);
            } else {
              setLockFilter(newValue === "locked");
            }
          }}
          placeholder="Any"
          options={[
            { value: "locked", label: "Locked" },
            { value: "unlocked", label: "Unlocked" },
          ]}
        />
      </div>
      <div className={filterComboboxClassName}>
        <Label className="text-xs font-semibold text-muted-foreground">Flagged paths</Label>
        <FilterCombobox
          value={hasFlaggedPathsFilter === null ? null : hasFlaggedPathsFilter ? "has-flagged-paths" : "no-flagged-paths"}
          onChange={(newValue) => {
            setPage(0);
            if (newValue === null || newValue === "all") {
              setHasFlaggedPathsFilter(null);
            } else {
              setHasFlaggedPathsFilter(newValue === "has-flagged-paths");
            }
          }}
          placeholder="Any"
          options={[
            { value: "has-flagged-paths", label: "Has flagged paths" },
            { value: "no-flagged-paths", label: "No flagged paths" },
          ]}
        />
      </div>
      <div className={filterComboboxClassName}>
        <Label className="text-xs font-semibold text-muted-foreground">Conflicts</Label>
        <FilterCombobox
          value={conflictsFilter === null ? null : conflictsFilter ? "has-conflicts" : "no-conflicts"}
          onChange={(newValue) => {
            setPage(0);
            if (newValue === null || newValue === "all") {
              setConflictsFilter(null);
            } else {
              setConflictsFilter(newValue === "has-conflicts");
            }
          }}
          placeholder="Any"
          options={[
            { value: "has-conflicts", label: "Has conflicts", icon: FileWarning },
            { value: "no-conflicts", label: "No conflicts", icon: CheckCircle2 },
          ]}
        />
      </div>
      <FlagStatusFilter value={flagStatusFilter} onChange={(v) => { setPage(0); setFlagStatusFilter(v); }} />
    </React.Fragment>
  );

  const handleBlockModal = (
    providerNameId: string,
    providerId: string,
    isBlocked: boolean,
    isOpen: boolean
  ) => {
    setIsBlockModalOpen(isOpen);
    setDirtyProviderNameId(providerNameId);
    setDirtyProviderId(providerId);
    setIsBlocked(isBlocked);
  };

  const { isError, isFetching } = providersQuery;
  const disabledRows = getDisabledRows();
  const loading = isFetching || isError;

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      <div className={fileFiltersBoxClassName}>
        <SearchInput
          label="Search providers"
          placeholder="Name or ID"
          onSearch={(value) => handleSearch(value)}
        />
        {renderFilters()}
      </div>
      <div className={cn(tableWrapperClassName, loading && "opacity-50 pointer-events-none")}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[14%]">
                <SortableTableColumnHeader
                  label={"Name Id"}
                  sortProperty={"providerNameId"}
                  getSort={() => sort}
                  setSort={(newSort) => changeSort(newSort)}
                  getOrder={() => order}
                  setOrder={(newOrder) => changeOrder(newOrder)}
                />
              </TableHead>
              <TableHead className="w-[5%]">Lock</TableHead>

              <TableHead className="w-[11%]">
                <SortableTableColumnHeader
                  label={"Status"}
                  sortProperty={"status"}
                  getSort={() => sort}
                  setSort={(newSort) => changeSort(newSort)}
                  getOrder={() => order}
                  setOrder={(newOrder) => changeOrder(newOrder)}
                />
              </TableHead>

              <TableHead className="w-[7%]">Progress</TableHead>

              <TableHead className="w-[8%]">
                <SortableTableColumnHeader
                  label={"Files"}
                  sortProperty={"totalNumberOfFiles"}
                  getSort={() => sort}
                  setSort={(newSort) => changeSort(newSort)}
                  getOrder={() => order}
                  setOrder={(newOrder) => changeOrder(newOrder)}
                />
              </TableHead>

              <TableHead className="w-[9%]">
                <SortableTableColumnHeader
                  label={"Size"}
                  sortProperty={"totalFilesSize"}
                  getSort={() => sort}
                  setSort={(newSort) => changeSort(newSort)}
                  getOrder={() => order}
                  setOrder={(newOrder) => changeOrder(newOrder)}
                />
              </TableHead>

              <TableHead className="w-[10%]">Flag Type</TableHead>

              <TableHead className="w-[10%]">
                Blocked By
              </TableHead>

              <TableHead className="w-[12%]">
                Flag Status
              </TableHead>

              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>

          <TableBody>
            {providers.map((providerEntity) => {
              const migratedPercentage = providerEntity.statistics.totalNumberOfFiles > 0
                ? ((providerEntity.statistics.filesMigrated + providerEntity.statistics.duplicateFilesSkipped) / providerEntity.statistics.totalNumberOfFiles) * 100
                : -1;
              const isDisabled = disabledRows.has(providerEntity.id);

              return (
                <TableRow
                  key={providerEntity.id}
                  className={cn("cursor-pointer", isDisabled && "opacity-50 pointer-events-none")}
                  tabIndex={0}
                  onClick={() =>
                    navigate(
                      `/organization/${organizationId}/provider/${providerEntity.id}`
                    )
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/organization/${organizationId}/provider/${providerEntity.id}`);
                    }
                  }}
                >
                  <TableCell className="whitespace-normal break-all">
                    <div className="flex items-center gap-1.5">
                      <TextHighlighter
                        text={providerEntity.providerNameId}
                        searchTerm={searchForProviderNameId}
                        level="body-sm"
                      />
                      {(providerEntity.statistics.unresolvedConflictCount ?? 0) > 0 && (
                        <TooltipProvider><Tooltip><TooltipTrigger asChild>
                          <FileWarning className="size-4 shrink-0 text-destructive" aria-label="Has unresolved conflicts" />
                        </TooltipTrigger><TooltipContent>Has unresolved conflicts</TooltipContent></Tooltip></TooltipProvider>
                      )}
                      {providerEntity.hasFlaggedPaths && (
                        <TooltipProvider><Tooltip><TooltipTrigger asChild>
                          <AlertTriangle className={cn("size-4 shrink-0", colorVariantClasses.warning.text)} aria-label="Has flagged paths" />
                        </TooltipTrigger><TooltipContent>Has flagged paths</TooltipContent></Tooltip></TooltipProvider>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {providerEntity.isLocked ? (
                      <Badge variant="primary"><Lock className="size-3" />Locked</Badge>
                    ) : (
                      <></>
                    )}
                  </TableCell>
                  <TableCell>
                    <MigrationStatusChip status={providerEntity.status} />
                  </TableCell>
                  <TableCell>
                    {providerEntity.statistics.totalNumberOfFiles > 0 ? (
                      <Badge variant={migratedPercentage > 99.99 ? "success" : "primary"}>
                        {migratedPercentage % 1 > 0.99 ||
                          migratedPercentage % 1 < 0.01
                            ? `${migratedPercentage.toFixed(0)}%`
                            : `${migratedPercentage.toFixed(2)}%`}
                      </Badge>
                    ) : (
                      <Badge variant="secondary">No files</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="neutral">{String(providerEntity.statistics.totalNumberOfFiles ?? 0)}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="neutral">{`${providerEntity.statistics.filesSizeInMb.toFixed(2)} MB`}</Badge>
                  </TableCell>

                  <TableCell>
                    {providerEntity.flagType ? (
                      <FlagTypeChip flagType={providerEntity.flagType} />
                    ) : (
                      <></>
                    )}
                  </TableCell>

                  <TableCell>
                    <span className="text-sm whitespace-normal break-words">
                      {getUserName(providerEntity.flaggedBy)}
                    </span>
                  </TableCell>

                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {providerEntity.flagType == null ||
                    providerEntity.flagType ===
                      FlagType.MigrationEntityBlockedByUser ? (
                      <Button
                        className={cn(
                          "w-full max-w-[150px] justify-start text-left",
                          providerEntity.flagType === FlagType.MigrationEntityBlockedByUser
                            ? colorVariantClasses.lime.text
                            : colorVariantClasses.warning.text
                        )}
                        variant="secondary"
                        aria-label="Block/Unblock"
                        disabled={
                          isDisabled
                          || userRole === UserRole.Viewer
                          || (!isAssignedUser && userRole !== UserRole.Admin)
                        }
                        size="sm"
                        onClick={() => {
                          providerEntity.flagType ===
                          FlagType.MigrationEntityBlockedByUser
                            ? handleBlockModal(
                                providerEntity.providerNameId,
                                providerEntity.id,
                                false,
                                true
                              )
                            : handleBlockModal(
                                providerEntity.providerNameId,
                                providerEntity.id,
                                true,
                                true
                              );
                        }}
                      >
                        {providerEntity.flagType ===
                        FlagType.MigrationEntityBlockedByUser ? (
                          <LockOpen className="size-4" />
                        ) : (
                          <Ban className="size-4" />
                        )}
                        {providerEntity.flagType ===
                        FlagType.MigrationEntityBlockedByUser
                          ? "Unblock"
                          : "Block"}
                      </Button>
                    ) : (
                      <></>
                    )}
                  </TableCell>

                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {getActiveButtonForExplorer(providerEntity, isDisabled)}
                  </TableCell>
                </TableRow>
              );
            })}
              {providers.length === 0 && !isFetching && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={99}>
                    <TableEmptyState title="No providers found" />
                  </TableCell>
                </TableRow>
              )}
          </TableBody>

        </Table>
        <TablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          rows={rows}
          onPageChange={(newPage) => { handleChangePage(newPage); }}
          onRowsPerPageChange={(val) => { setRowsPerPage(val); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </div>

      {/* Block Modal */}
      <ConfirmMigrationEntityBlockUpdateModal
        open={isBlockModalOpen}
        isBlocked={isBlocked}
        entityKey={dirtyProviderNameId}
        config={MIGRATION_ENTITY_BLOCK_UPDATE_CONFIGS.PROVIDER}
        onClose={() => {
          handleBlockModal("", "", false, false);
        }}
        onConfirm={async () => {
          await providersRepository.updateProviderBlock(
            dirtyProviderId,
            isBlocked
          );
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PROVIDERS] }),
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PROVIDER, dirtyProviderId] }),
          ]);
          handleBlockModal("", "", false, false);
          toast.success(isBlocked ? "Blocked" : "Unblocked");
        }}
      />
    </div>
  );
};

export default ProvidersTable;
